import React, { useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import { splitNumberDigits } from 'helpers/math'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import { useHistory } from 'react-router-dom'
import style from './TeacherPlanReport.module.scss'

const TeacherPlanReport = () => {
  const [catalog, setCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const {
    refWrapper,
    data,
    setData,
    isLoading,
    setIsLoading,
    getTdClassNames,
  } = useTableFixedHeader()

  const { translate } = useLocalization({
    section: 'teacher_plan',
    subsection: 'all_teachers',
    dict: {
      filter: 'Filter',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
    },
  })

  const filterCnf = [
    {
      type: 'select-multi',
      label: 'Локация',
      nameState: 'location',
      placeholder: 'Локация',
      values:
        catalog.locations?.sort((a, b) => (a.name > b.name ? 1 : -1)) || [],
      option_value_key: 'id',
      option_label_key: 'name',
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from_1', 'date_until_1'],
      label: 'Период 2024-2025',
      isRequired: true,
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from_2', 'date_until_2'],
      label: 'Период 2025-2026',
      isRequired: true,
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from_3', 'date_until_3'],
      label: 'Период 2026-2027',
      isRequired: true,
    },
    {
      type: 'select',
      label: 'Формат занятости',
      nameState: 'parttime',
      placeholder: 'Все',
      values: [
        { id: true, name: 'Part-time' },
        { id: false, name: 'Full-time' },
      ],
      option_value_key: 'id',
      option_label_key: 'name',
    },
  ]

  // Загрузка каталога локаций
  useEffect(() => {
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/teacher_dynamics_report',
    })
      .then(({ data }) => {
        setCatalog({ locations: data.locations || [] })
        setData({ locations: [] })
        setIsLoading(false)
      })
      .catch(() => setIsLoading(false))
  }, [])

  // Функция для преобразования минут в ак. часы (1 ак. час = 45 мин)
  const minutesToHours = (minutes) => {
    return Math.round(minutes / 45)
  }

  const getMonthName = (monthNum) => {
    const months = [
      'Январь',
      'Февраль',
      'Март',
      'Апрель',
      'Май',
      'Июнь',
      'Июль',
      'Август',
      'Сентябрь',
      'Октябрь',
      'Ноябрь',
      'Декабрь',
    ]
    return months[monthNum - 1] || ''
  }

  const calculateAverage = (hours, count) => {
    if (count === 0) return 0
    return Math.round((hours / count) * 10) / 10
  }

  // Загрузка данных при изменении фильтра
  useEffect(() => {
    if (
      !filter ||
      !filter.date_from_1 ||
      !filter.date_until_1 ||
      !filter.date_from_2 ||
      !filter.date_until_2 ||
      !filter.date_from_3 ||
      !filter.date_until_3
    ) {
      setData({ locations: [] })
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    const params = {
      date_from_1: filter.date_from_1,
      date_until_1: filter.date_until_1,
      date_from_2: filter.date_from_2,
      date_until_2: filter.date_until_2,
      date_from_3: filter.date_from_3,
      date_until_3: filter.date_until_3,
    }

    if (filter.parttime !== undefined && filter.parttime !== null) {
      params.parttime = filter.parttime
    }

    if (filter.location && filter.location.length > 0) {
      params.location = Array.isArray(filter.location)
        ? filter.location.join(',')
        : filter.location
    }

    superAxiosRequest({
      method: 'get',
      link: 'progress_report/teacher_dynamics_report',
      params,
    })
      .then(({ data }) => {
        const allMonths = new Set()
        const years = ['2024_2025', '2025_2026', '2026_2027']

        years.forEach((year) => {
          if (data[year]) {
            Object.keys(data[year]).forEach((locId) => {
              if (locId === 'total_teachers') return
              const locData = data[year][locId]
              Object.keys(locData).forEach((key) => {
                if (key !== 'location_id') {
                  allMonths.add(parseInt(key))
                }
              })
            })
          }
        })

        const sortedMonths = Array.from(allMonths).sort((a, b) => a - b)

        const transformedData = (catalog.locations || [])
          .filter((location) => {
            if (filter.location && filter.location.length > 0) {
              return filter.location.includes(location.id)
            }
            return (
              data['2024_2025']?.[location.id] ||
              data['2025_2026']?.[location.id] ||
              data['2026_2027']?.[location.id]
            )
          })
          .map((location) => {
            const locationData = {}

            sortedMonths.forEach((month) => {
              const monthName = getMonthName(month)

              // === 2024-2025 ===
              const data2024_2025 = data['2024_2025']?.[location.id]?.[month]
              if (data2024_2025) {
                locationData[`pt_${monthName}_2024_2025`] = {
                  hours: minutesToHours(data2024_2025.part_time?.minutes || 0),
                  count: data2024_2025.part_time?.teachers || 0,
                }
                locationData[`ft_${monthName}_2024_2025`] = {
                  hours: minutesToHours(data2024_2025.full_time?.minutes || 0),
                  count: data2024_2025.full_time?.teachers || 0,
                }
              }

              // === 2025-2026 ===
              const data2025_2026 = data['2025_2026']?.[location.id]?.[month]
              if (data2025_2026) {
                locationData[`pt_${monthName}_2025_2026`] = {
                  hours: minutesToHours(data2025_2026.part_time?.minutes || 0),
                  count: data2025_2026.part_time?.teachers || 0,
                }
                locationData[`ft_${monthName}_2025_2026`] = {
                  hours: minutesToHours(data2025_2026.full_time?.minutes || 0),
                  count: data2025_2026.full_time?.teachers || 0,
                }
              }

              // === 2026-2027 (план + факт) ===
              const data2026_2027 = data['2026_2027']?.[location.id]?.[month]
              if (data2026_2027) {
                const factData = data2026_2027.fact
                const planData = data2026_2027.plan

                const planPtMinutes = planData?.part_time?.minutes || 0
                const planPtCount = planData?.part_time?.teachers || 0
                const planFtMinutes = planData?.full_time?.minutes || 0
                const planFtCount = planData?.full_time?.teachers || 0

                const factPtMinutes = factData?.part_time?.minutes || 0
                const factPtCount = factData?.part_time?.teachers || 0
                const factFtMinutes = factData?.full_time?.minutes || 0
                const factFtCount = factData?.full_time?.teachers || 0

                locationData[`pt_${monthName}_2026_2027_plan`] = {
                  hours: planPtMinutes,
                  count: planPtCount,
                }
                locationData[`pt_${monthName}_2026_2027_fact`] = {
                  hours: minutesToHours(factPtMinutes),
                  count: factPtCount,
                }

                locationData[`ft_${monthName}_2026_2027_plan`] = {
                  hours: planFtMinutes,
                  count: planFtCount,
                }
                locationData[`ft_${monthName}_2026_2027_fact`] = {
                  hours: minutesToHours(factFtMinutes),
                  count: factFtCount,
                }
              }
            })

            return {
              id: location.id,
              name: location.name,
              data: locationData,
              months: sortedMonths.map((m) => ({
                num: m,
                name: getMonthName(m),
              })),
            }
          })

        setData({
          locations: transformedData,
          totalTeachers2024_2025: data['2024_2025']?.total_teachers || 0,
          totalTeachers2025_2026: data['2025_2026']?.total_teachers || 0,
          totalTeachers2026_2027: data['2026_2027']?.total_teachers || 0,
        })
        setIsLoading(false)
      })
      .catch(() => {
        setData({ locations: [] })
        setIsLoading(false)
      })
  }, [
    filter?.date_from_1,
    filter?.date_until_1,
    filter?.date_from_2,
    filter?.date_until_2,
    filter?.date_from_3,
    filter?.date_until_3,
    filter?.location,
    filter?.parttime,
    catalog.locations,
  ])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/teacher-plan') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  // === ТОТАЛЫ ПО ЛОКАЦИИ ===
  const calculateLocationTotals = (location) => {
    const totals = {
      hours_24_25: 0,
      count_24_25: 0,
      hours_25_26: 0,
      count_25_26: 0,
      plan_hours_26_27: 0,
      plan_count_26_27: 0,
      fact_hours_26_27: 0,
      fact_count_26_27: 0,
      diff_26_27: 0,
      diff_count_26_27: 0,
    }

    location.months.forEach((month) => {
      const mName = month.name

      const pt24 = location.data[`pt_${mName}_2024_2025`] || {
        hours: 0,
        count: 0,
      }
      const ft24 = location.data[`ft_${mName}_2024_2025`] || {
        hours: 0,
        count: 0,
      }
      totals.hours_24_25 += pt24.hours + ft24.hours
      totals.count_24_25 += pt24.count + ft24.count

      const pt25 = location.data[`pt_${mName}_2025_2026`] || {
        hours: 0,
        count: 0,
      }
      const ft25 = location.data[`ft_${mName}_2025_2026`] || {
        hours: 0,
        count: 0,
      }
      totals.hours_25_26 += pt25.hours + ft25.hours
      totals.count_25_26 += pt25.count + ft25.count

      const ptPlan26 = location.data[`pt_${mName}_2026_2027_plan`] || {
        hours: 0,
        count: 0,
      }
      const ftPlan26 = location.data[`ft_${mName}_2026_2027_plan`] || {
        hours: 0,
        count: 0,
      }
      totals.plan_hours_26_27 += ptPlan26.hours + ftPlan26.hours
      totals.plan_count_26_27 += ptPlan26.count + ftPlan26.count

      const ptFact26 = location.data[`pt_${mName}_2026_2027_fact`] || {
        hours: 0,
        count: 0,
      }
      const ftFact26 = location.data[`ft_${mName}_2026_2027_fact`] || {
        hours: 0,
        count: 0,
      }
      totals.fact_hours_26_27 += ptFact26.hours + ftFact26.hours
      totals.fact_count_26_27 += ptFact26.count + ftFact26.count
    })

    // Разница = Факт - План
    totals.diff_26_27 = totals.fact_hours_26_27 - totals.plan_hours_26_27
    totals.diff_count_26_27 = totals.fact_count_26_27 - totals.plan_count_26_27
    return totals
  }

  const calculateGrandTotals = (locations) => {
    const totals = {
      hours_24_25: 0,
      count_24_25: 0,
      hours_25_26: 0,
      count_25_26: 0,
      plan_hours_26_27: 0,
      plan_count_26_27: 0,
      fact_hours_26_27: 0,
      fact_count_26_27: 0,
      diff_26_27: 0,
      diff_count_26_27: 0,
    }

    locations.forEach((loc) => {
      const locTotals = calculateLocationTotals(loc)
      totals.hours_24_25 += locTotals.hours_24_25
      totals.count_24_25 += locTotals.count_24_25
      totals.hours_25_26 += locTotals.hours_25_26
      totals.count_25_26 += locTotals.count_25_26
      totals.plan_hours_26_27 += locTotals.plan_hours_26_27
      totals.plan_count_26_27 += locTotals.plan_count_26_27
      totals.fact_hours_26_27 += locTotals.fact_hours_26_27
      totals.fact_count_26_27 += locTotals.fact_count_26_27
    })

    // Разница = Факт - План
    totals.diff_26_27 = totals.fact_hours_26_27 - totals.plan_hours_26_27
    totals.diff_count_26_27 = totals.fact_count_26_27 - totals.plan_count_26_27
    return totals
  }

  // Средние для второй таблицы
  const calculateAverageData = (location) => {
    const averages = {
      total_24_25: { hours: 0, count: 0, avg: 0 },
      total_25_26: { hours: 0, count: 0, avg: 0 },
      total_plan_26_27: { hours: 0, count: 0, avg: 0 },
      total_fact_26_27: { hours: 0, count: 0, avg: 0 },
    }

    location.months.forEach((month) => {
      const mName = month.name

      const pt24 = location.data[`pt_${mName}_2024_2025`] || {
        hours: 0,
        count: 0,
      }
      const ft24 = location.data[`ft_${mName}_2024_2025`] || {
        hours: 0,
        count: 0,
      }
      averages.total_24_25.hours += pt24.hours + ft24.hours
      averages.total_24_25.count += pt24.count + ft24.count

      const pt25 = location.data[`pt_${mName}_2025_2026`] || {
        hours: 0,
        count: 0,
      }
      const ft25 = location.data[`ft_${mName}_2025_2026`] || {
        hours: 0,
        count: 0,
      }
      averages.total_25_26.hours += pt25.hours + ft25.hours
      averages.total_25_26.count += pt25.count + ft25.count

      const ptPlan26 = location.data[`pt_${mName}_2026_2027_plan`] || {
        hours: 0,
        count: 0,
      }
      const ftPlan26 = location.data[`ft_${mName}_2026_2027_plan`] || {
        hours: 0,
        count: 0,
      }
      const ptFact26 = location.data[`pt_${mName}_2026_2027_fact`] || {
        hours: 0,
        count: 0,
      }
      const ftFact26 = location.data[`ft_${mName}_2026_2027_fact`] || {
        hours: 0,
        count: 0,
      }

      averages.total_plan_26_27.hours += ptPlan26.hours + ftPlan26.hours
      averages.total_plan_26_27.count += ptPlan26.count + ftPlan26.count
      averages.total_fact_26_27.hours += ptFact26.hours + ftFact26.hours
      averages.total_fact_26_27.count += ptFact26.count + ftFact26.count
    })

    averages.total_24_25.avg = calculateAverage(
      averages.total_24_25.hours,
      averages.total_24_25.count,
    )
    averages.total_25_26.avg = calculateAverage(
      averages.total_25_26.hours,
      averages.total_25_26.count,
    )
    averages.total_plan_26_27.avg = calculateAverage(
      averages.total_plan_26_27.hours,
      averages.total_plan_26_27.count,
    )
    averages.total_fact_26_27.avg = calculateAverage(
      averages.total_fact_26_27.hours,
      averages.total_fact_26_27.count,
    )

    return averages
  }

  // === ЭКСПОРТ В EXCEL ===
  const downloadExcel = (data) => {
    import('xlsx-js-style').then((XLSX) => {
      const wb = XLSX.utils.book_new()

      // ====== ПЕРВАЯ ТАБЛИЦА ======
      const wsData1 = []
      const merges1 = []
      const rowColors1 = []
      // Красные ячейки ТОЛЬКО для строк "Итого по локации" и "Общее итого"
      const redCells1 = []

      const headerRow1 = [
        'Локация',
        'PartTime/Fulltime',
        'Период',
        '2024-2025 Факт',
        '',
        '2025-2026 Факт',
        '',
        '2026-2027 План',
        '',
        '2026-2027 Факт',
        '',
        '2026-2027 Разница',
        '',
      ]
      const headerRow2 = [
        '',
        '',
        '',
        'отработанные ак часы',
        'количество учителей',
        'отработанные ак часы',
        'количество учителей',
        'ак часы',
        'количество учителей',
        'отработанные ак часы',
        'количество учителей',
        'ак часы',
        'количество учителей',
      ]
      wsData1.push(headerRow1)
      wsData1.push(headerRow2)

      merges1.push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } })
      merges1.push({ s: { r: 0, c: 1 }, e: { r: 1, c: 1 } })
      merges1.push({ s: { r: 0, c: 2 }, e: { r: 1, c: 2 } })
      merges1.push({ s: { r: 0, c: 3 }, e: { r: 0, c: 4 } })
      merges1.push({ s: { r: 0, c: 5 }, e: { r: 0, c: 6 } })
      merges1.push({ s: { r: 0, c: 7 }, e: { r: 0, c: 8 } })
      merges1.push({ s: { r: 0, c: 9 }, e: { r: 0, c: 10 } })
      merges1.push({ s: { r: 0, c: 11 }, e: { r: 0, c: 12 } })

      let currentRow = 2

      data?.locations?.forEach((location) => {
        const months = location.months || []
        const startLocRow = currentRow

        // PT
        const startPTRow = currentRow
        months.forEach((month) => {
          const mName = month.name
          const ptPlan26 = location.data[`pt_${mName}_2026_2027_plan`] || {
            hours: 0,
            count: 0,
          }
          const ptFact26 = location.data[`pt_${mName}_2026_2027_fact`] || {
            hours: 0,
            count: 0,
          }

          // Разница = Факт - План
          const diffHours = ptFact26.hours - ptPlan26.hours
          const diffCount = ptFact26.count - ptPlan26.count

          wsData1.push([
            '',
            '',
            mName,
            splitNumberDigits(
              location.data[`pt_${mName}_2024_2025`]?.hours || 0,
            ),
            splitNumberDigits(
              location.data[`pt_${mName}_2024_2025`]?.count || 0,
            ),
            splitNumberDigits(
              location.data[`pt_${mName}_2025_2026`]?.hours || 0,
            ),
            splitNumberDigits(
              location.data[`pt_${mName}_2025_2026`]?.count || 0,
            ),
            splitNumberDigits(ptPlan26.hours),
            splitNumberDigits(ptPlan26.count),
            splitNumberDigits(ptFact26.hours),
            splitNumberDigits(ptFact26.count),
            splitNumberDigits(diffHours),
            splitNumberDigits(diffCount),
          ])
          // НЕ добавляем в redCells1 для строк по месяцам
          currentRow++
        })
        merges1.push({
          s: { r: startPTRow, c: 1 },
          e: { r: currentRow - 1, c: 1 },
        })
        wsData1[startPTRow][1] = 'PT'

        // FT
        const startFTRow = currentRow
        months.forEach((month) => {
          const mName = month.name
          const ftPlan26 = location.data[`ft_${mName}_2026_2027_plan`] || {
            hours: 0,
            count: 0,
          }
          const ftFact26 = location.data[`ft_${mName}_2026_2027_fact`] || {
            hours: 0,
            count: 0,
          }

          // Разница = Факт - План
          const diffHours = ftFact26.hours - ftPlan26.hours
          const diffCount = ftFact26.count - ftPlan26.count

          wsData1.push([
            '',
            '',
            mName,
            splitNumberDigits(
              location.data[`ft_${mName}_2024_2025`]?.hours || 0,
            ),
            splitNumberDigits(
              location.data[`ft_${mName}_2024_2025`]?.count || 0,
            ),
            splitNumberDigits(
              location.data[`ft_${mName}_2025_2026`]?.hours || 0,
            ),
            splitNumberDigits(
              location.data[`ft_${mName}_2025_2026`]?.count || 0,
            ),
            splitNumberDigits(ftPlan26.hours),
            splitNumberDigits(ftPlan26.count),
            splitNumberDigits(ftFact26.hours),
            splitNumberDigits(ftFact26.count),
            splitNumberDigits(diffHours),
            splitNumberDigits(diffCount),
          ])
          // НЕ добавляем в redCells1 для строк по месяцам
          currentRow++
        })
        merges1.push({
          s: { r: startFTRow, c: 1 },
          e: { r: currentRow - 1, c: 1 },
        })
        wsData1[startFTRow][1] = 'FT'

        merges1.push({
          s: { r: startLocRow, c: 0 },
          e: { r: currentRow - 1, c: 0 },
        })
        wsData1[startLocRow][0] = location.name

        // === СТРОКА "ИТОГО ПО ЛОКАЦИИ" — здесь красные ячейки ===
        const locTotals = calculateLocationTotals(location)
        const totalRow = [
          `Итого: ${location.name}`,
          '',
          '',
          splitNumberDigits(locTotals.hours_24_25),
          splitNumberDigits(locTotals.count_24_25),
          splitNumberDigits(locTotals.hours_25_26),
          splitNumberDigits(locTotals.count_25_26),
          splitNumberDigits(locTotals.plan_hours_26_27),
          splitNumberDigits(locTotals.plan_count_26_27),
          splitNumberDigits(locTotals.fact_hours_26_27),
          splitNumberDigits(locTotals.fact_count_26_27),
          splitNumberDigits(locTotals.diff_26_27),
          splitNumberDigits(locTotals.diff_count_26_27),
        ]
        wsData1.push(totalRow)
        merges1.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 2 } })
        rowColors1.push({ rowIndex: currentRow, color: '5a8dee' })
        // Красная подсветка ТОЛЬКО здесь (итого по локации)
        if (locTotals.diff_26_27 < 0)
          redCells1.push({ r: currentRow, c: 11, total: true })
        if (locTotals.diff_count_26_27 < 0)
          redCells1.push({ r: currentRow, c: 12, total: true })
        currentRow++
      })

      if (data?.locations?.length > 0) {
        const grandTotals = calculateGrandTotals(data.locations)
        const totalTeachers24_25 =
          data.totalTeachers2024_2025 || grandTotals.count_24_25
        const totalTeachers25_26 =
          data.totalTeachers2025_2026 || grandTotals.count_25_26
        const totalTeachersPlan26_27 = grandTotals.plan_count_26_27
        const totalTeachersFact26_27 =
          data.totalTeachers2026_2027 || grandTotals.fact_count_26_27

        // Разница = Факт - План
        const grandDiffCount = totalTeachersFact26_27 - totalTeachersPlan26_27

        const grandRow = [
          'Общее итого',
          '',
          '',
          splitNumberDigits(grandTotals.hours_24_25),
          splitNumberDigits(totalTeachers24_25),
          splitNumberDigits(grandTotals.hours_25_26),
          splitNumberDigits(totalTeachers25_26),
          splitNumberDigits(grandTotals.plan_hours_26_27),
          splitNumberDigits(totalTeachersPlan26_27),
          splitNumberDigits(grandTotals.fact_hours_26_27),
          splitNumberDigits(totalTeachersFact26_27),
          splitNumberDigits(grandTotals.diff_26_27),
          splitNumberDigits(grandDiffCount),
        ]
        wsData1.push(grandRow)
        merges1.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 2 } })
        rowColors1.push({ rowIndex: currentRow, color: '5a8dee' })
        // Красная подсветка ТОЛЬКО здесь (общее итого)
        if (grandTotals.diff_26_27 < 0)
          redCells1.push({ r: currentRow, c: 11, total: true })
        if (grandDiffCount < 0)
          redCells1.push({ r: currentRow, c: 12, total: true })
      }

      const ws1 = XLSX.utils.aoa_to_sheet(wsData1)
      ws1['!merges'] = merges1

      ws1['!cols'] = Array(13).fill({ wch: 12 })
      ws1['!cols'][0] = { wch: 20 }
      ws1['!cols'][1] = { wch: 15 }

      ws1['!rows'] = [{ hpx: 40 }, { hpx: 40 }]

      const borderStyle = {
        top: { style: 'thin', color: { rgb: 'B4C6E7' } },
        bottom: { style: 'thin', color: { rgb: 'B4C6E7' } },
        left: { style: 'thin', color: { rgb: 'B4C6E7' } },
        right: { style: 'thin', color: { rgb: 'B4C6E7' } },
      }

      const range1 = XLSX.utils.decode_range(ws1['!ref'])
      for (let R = range1.s.r; R <= range1.e.r; ++R) {
        for (let C = range1.s.c; C <= range1.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
          if (!ws1[cellAddress]) ws1[cellAddress] = { t: 's', v: '' }

          let style = {
            font: { sz: 10, color: { rgb: '333333' } },
            alignment: {
              horizontal: 'center',
              vertical: 'center',
              wrapText: true,
            },
            border: borderStyle,
          }

          if (R < 2) {
            style.font = { bold: true, sz: 12, color: { rgb: '1a2c3e' } }
            style.fill = { fgColor: { rgb: 'E8E8E8' } }
          }

          const colorInfo = rowColors1.find((rc) => rc.rowIndex === R)
          if (colorInfo) {
            style.fill = { fgColor: { rgb: colorInfo.color } }
            style.font = { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }
          }

          // Красная подсветка ТОЛЬКО для строк итого
          const isRedCell = redCells1.find((rc) => rc.r === R && rc.c === C)
          if (isRedCell) {
            style.font = { sz: 13, bold: true, color: { rgb: 'FF0000' } }
          }

          ws1[cellAddress].s = style
        }
      }

      XLSX.utils.book_append_sheet(wb, ws1, 'План по учителям')

      // ====== ВТОРАЯ ТАБЛИЦА (средняя загрузка) ======
      const wsData2 = []
      const merges2 = []
      const rowColors2 = []
      const redCells2 = []

      const avgHeaderRow = [
        'Локация',
        'PartTime/Fulltime',
        'Период',
        '2024-2025 Факт',
        '2025-2026 Факт',
        '2026-2027 План',
        '2026-2027 Факт',
        '2026-2027 Разница',
      ]
      wsData2.push(avgHeaderRow)

      let currentRow2 = 1

      data?.locations?.forEach((location) => {
        const months = location.months || []
        const startLocRow = currentRow2

        // PT
        const startPTRow = currentRow2
        months.forEach((month) => {
          const mName = month.name
          const pt24_25 = location.data[`pt_${mName}_2024_2025`] || {
            hours: 0,
            count: 0,
          }
          const pt25_26 = location.data[`pt_${mName}_2025_2026`] || {
            hours: 0,
            count: 0,
          }
          const ptPlan26 = location.data[`pt_${mName}_2026_2027_plan`] || {
            hours: 0,
            count: 0,
          }
          const ptFact26 = location.data[`pt_${mName}_2026_2027_fact`] || {
            hours: 0,
            count: 0,
          }

          const avg24_25 = calculateAverage(pt24_25.hours, pt24_25.count)
          const avg25_26 = calculateAverage(pt25_26.hours, pt25_26.count)
          const avgPlan26 = calculateAverage(ptPlan26.hours, ptPlan26.count)
          const avgFact26 = calculateAverage(ptFact26.hours, ptFact26.count)
          // Разница = Факт - План
          const diff = Math.round((avgFact26 - avgPlan26) * 10) / 10

          wsData2.push([
            '',
            '',
            mName,
            splitNumberDigits(avg24_25),
            splitNumberDigits(avg25_26),
            splitNumberDigits(avgPlan26),
            splitNumberDigits(avgFact26),
            splitNumberDigits(diff),
          ])
          // НЕ добавляем в redCells2 для строк по месяцам
          currentRow2++
        })
        merges2.push({
          s: { r: startPTRow, c: 1 },
          e: { r: currentRow2 - 1, c: 1 },
        })
        wsData2[startPTRow][1] = 'PT'

        // FT
        const startFTRow = currentRow2
        months.forEach((month) => {
          const mName = month.name
          const ft24_25 = location.data[`ft_${mName}_2024_2025`] || {
            hours: 0,
            count: 0,
          }
          const ft25_26 = location.data[`ft_${mName}_2025_2026`] || {
            hours: 0,
            count: 0,
          }
          const ftPlan26 = location.data[`ft_${mName}_2026_2027_plan`] || {
            hours: 0,
            count: 0,
          }
          const ftFact26 = location.data[`ft_${mName}_2026_2027_fact`] || {
            hours: 0,
            count: 0,
          }

          const avg24_25 = calculateAverage(ft24_25.hours, ft24_25.count)
          const avg25_26 = calculateAverage(ft25_26.hours, ft25_26.count)
          const avgPlan26 = calculateAverage(ftPlan26.hours, ftPlan26.count)
          const avgFact26 = calculateAverage(ftFact26.hours, ftFact26.count)
          // Разница = Факт - План
          const diff = Math.round((avgFact26 - avgPlan26) * 10) / 10

          wsData2.push([
            '',
            '',
            mName,
            splitNumberDigits(avg24_25),
            splitNumberDigits(avg25_26),
            splitNumberDigits(avgPlan26),
            splitNumberDigits(avgFact26),
            splitNumberDigits(diff),
          ])
          // НЕ добавляем в redCells2 для строк по месяцам
          currentRow2++
        })
        merges2.push({
          s: { r: startFTRow, c: 1 },
          e: { r: currentRow2 - 1, c: 1 },
        })
        wsData2[startFTRow][1] = 'FT'

        merges2.push({
          s: { r: startLocRow, c: 0 },
          e: { r: currentRow2 - 1, c: 0 },
        })
        wsData2[startLocRow][0] = location.name

        // === СТРОКА "ИТОГО ПО ЛОКАЦИИ" — здесь красные ячейки ===
        const averages = calculateAverageData(location)
        const totalDiff =
          Math.round(
            (averages.total_fact_26_27.avg - averages.total_plan_26_27.avg) *
              10,
          ) / 10
        const totalRow = [
          'Итого по локации',
          '',
          '',
          splitNumberDigits(averages.total_24_25.avg),
          splitNumberDigits(averages.total_25_26.avg),
          splitNumberDigits(averages.total_plan_26_27.avg),
          splitNumberDigits(averages.total_fact_26_27.avg),
          splitNumberDigits(totalDiff),
        ]
        wsData2.push(totalRow)
        merges2.push({
          s: { r: currentRow2, c: 0 },
          e: { r: currentRow2, c: 2 },
        })
        rowColors2.push({ rowIndex: currentRow2, color: '5a8dee' })
        // Красная подсветка ТОЛЬКО здесь (итого по локации)
        if (totalDiff < 0) redCells2.push({ r: currentRow2, c: 7, total: true })
        currentRow2++
      })

      if (data?.locations?.length > 0) {
        const grandTotals = calculateGrandTotals(data.locations)
        const grandAvg24_25 = calculateAverage(
          grandTotals.hours_24_25,
          grandTotals.count_24_25,
        )
        const grandAvg25_26 = calculateAverage(
          grandTotals.hours_25_26,
          grandTotals.count_25_26,
        )
        const grandAvgPlan26_27 = calculateAverage(
          grandTotals.plan_hours_26_27,
          grandTotals.plan_count_26_27,
        )
        const grandAvgFact26_27 = calculateAverage(
          grandTotals.fact_hours_26_27,
          grandTotals.fact_count_26_27,
        )
        // Разница = Факт - План
        const grandDiff =
          Math.round((grandAvgFact26_27 - grandAvgPlan26_27) * 10) / 10

        const grandRow = [
          'Итого общее',
          '',
          '',
          splitNumberDigits(grandAvg24_25),
          splitNumberDigits(grandAvg25_26),
          splitNumberDigits(grandAvgPlan26_27),
          splitNumberDigits(grandAvgFact26_27),
          splitNumberDigits(grandDiff),
        ]
        wsData2.push(grandRow)
        merges2.push({
          s: { r: currentRow2, c: 0 },
          e: { r: currentRow2, c: 2 },
        })
        rowColors2.push({ rowIndex: currentRow2, color: '5a8dee' })
        // Красная подсветка ТОЛЬКО здесь (общее итого)
        if (grandDiff < 0) redCells2.push({ r: currentRow2, c: 7, total: true })
      }

      const ws2 = XLSX.utils.aoa_to_sheet(wsData2)
      ws2['!merges'] = merges2

      ws2['!cols'] = [
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ]

      ws2['!rows'] = [{ hpx: 40 }]

      const range2 = XLSX.utils.decode_range(ws2['!ref'])
      for (let R = range2.s.r; R <= range2.e.r; ++R) {
        for (let C = range2.s.c; C <= range2.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
          if (!ws2[cellAddress]) ws2[cellAddress] = { t: 's', v: '' }

          let style = {
            font: { sz: 10, color: { rgb: '333333' } },
            alignment: {
              horizontal: 'center',
              vertical: 'center',
              wrapText: true,
            },
            border: borderStyle,
          }

          if (R === 0) {
            style.font = { bold: true, sz: 12, color: { rgb: '1a2c3e' } }
            style.fill = { fgColor: { rgb: 'E8E8E8' } }
          }

          const colorInfo = rowColors2.find((rc) => rc.rowIndex === R)
          if (colorInfo) {
            style.fill = { fgColor: { rgb: colorInfo.color } }
            style.font = { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }
          }

          // Красная подсветка ТОЛЬКО для строк итого
          const isRedCell = redCells2.find((rc) => rc.r === R && rc.c === C)
          if (isRedCell) {
            style.font = { sz: 13, bold: true, color: { rgb: 'FF0000' } }
          }

          ws2[cellAddress].s = style
        }
      }

      XLSX.utils.book_append_sheet(wb, ws2, 'Средняя загрузка по учителю')

      XLSX.writeFile(wb, 'План_по_учителям.xlsx')
    })
  }

  const locations = data?.locations || []
  const isFilterEmpty = !filterTextValues.length
  const hasRequiredFilters =
    filter &&
    filter.date_from_1 &&
    filter.date_until_1 &&
    filter.date_from_2 &&
    filter.date_until_2 &&
    filter.date_from_3 &&
    filter.date_until_3

  // Стиль для минусовых значений в строках итого
  const negativeStyle = {
    color: 'red',
    fontWeight: 'bold',
  }

  return (
    <>
      <ContentHeader title='План по учителям' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder='Search...'
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter || {}}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : !hasRequiredFilters ? (
        <h1 className={st.noData}>Выберите периоды для отображения данных</h1>
      ) : data?.locations && data.locations.length > 0 ? (
        <>
          <div style={{ padding: '10px', marginBottom: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>

          {/* ПЕРВАЯ ТАБЛИЦА */}
          <div
            style={{ padding: '10px' }}
            className={st.tableWrapper}
            ref={refWrapper}
          >
            <table className={st.table}>
              <thead className={style.tableHeader}>
                <tr>
                  <th className={style.stickyColumn} rowSpan={2}>
                    Локация
                  </th>
                  <th rowSpan={2}>PartTime/Fulltime</th>
                  <th rowSpan={2}>Период</th>
                  <th colSpan={2}>2024-2025 Факт</th>
                  <th colSpan={2}>2025-2026 Факт</th>
                  <th colSpan={2}>2026-2027 План</th>
                  <th colSpan={2}>2026-2027 Факт</th>
                  <th colSpan={2}>2026-2027 Разница</th>
                </tr>
                <tr>
                  <th>отработанные ак часы</th>
                  <th>количество учителей</th>
                  <th>отработанные ак часы</th>
                  <th>количество учителей</th>
                  <th>ак часы</th>
                  <th>количество учителей</th>
                  <th>отработанные ак часы</th>
                  <th>количество учителей</th>
                  <th>ак часы</th>
                  <th>количество учителей</th>
                </tr>
              </thead>
              <tbody className={`${st.tbody} ${st.tbodyStriped}`}>
                {locations.map((location) => {
                  const months = location.months || []
                  return (
                    <React.Fragment key={location.id}>
                      {months.map((month, idx) => {
                        const ptPlan26 = location.data[
                          `pt_${month.name}_2026_2027_plan`
                        ] || { hours: 0, count: 0 }
                        const ptFact26 = location.data[
                          `pt_${month.name}_2026_2027_fact`
                        ] || { hours: 0, count: 0 }
                        // Разница = Факт - План (без подсветки для строк по месяцам)
                        const ptDiffHours = ptFact26.hours - ptPlan26.hours
                        const ptDiffCount = ptFact26.count - ptPlan26.count
                        return (
                          <tr key={`pt-${month.num}`}>
                            {idx === 0 && (
                              <td
                                className={getTdClassNames({
                                  classes: [st.color, st.fw500],
                                })}
                                rowSpan={months.length * 2}
                              >
                                {location.name}
                              </td>
                            )}
                            {idx === 0 && <td rowSpan={months.length}>PT</td>}
                            <td>{month.name}</td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(
                                location.data[`pt_${month.name}_2024_2025`]
                                  ?.hours || 0,
                              )}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(
                                location.data[`pt_${month.name}_2024_2025`]
                                  ?.count || 0,
                              )}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(
                                location.data[`pt_${month.name}_2025_2026`]
                                  ?.hours || 0,
                              )}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(
                                location.data[`pt_${month.name}_2025_2026`]
                                  ?.count || 0,
                              )}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ptPlan26.hours)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ptPlan26.count)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ptFact26.hours)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ptFact26.count)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ptDiffHours)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ptDiffCount)}
                            </td>
                          </tr>
                        )
                      })}
                      {months.map((month, idx) => {
                        const ftPlan26 = location.data[
                          `ft_${month.name}_2026_2027_plan`
                        ] || { hours: 0, count: 0 }
                        const ftFact26 = location.data[
                          `ft_${month.name}_2026_2027_fact`
                        ] || { hours: 0, count: 0 }
                        // Разница = Факт - План (без подсветки для строк по месяцам)
                        const ftDiffHours = ftFact26.hours - ftPlan26.hours
                        const ftDiffCount = ftFact26.count - ftPlan26.count
                        return (
                          <tr key={`ft-${month.num}`}>
                            {idx === 0 && <td rowSpan={months.length}>FT</td>}
                            <td>{month.name}</td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(
                                location.data[`ft_${month.name}_2024_2025`]
                                  ?.hours || 0,
                              )}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(
                                location.data[`ft_${month.name}_2024_2025`]
                                  ?.count || 0,
                              )}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(
                                location.data[`ft_${month.name}_2025_2026`]
                                  ?.hours || 0,
                              )}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(
                                location.data[`ft_${month.name}_2025_2026`]
                                  ?.count || 0,
                              )}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ftPlan26.hours)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ftPlan26.count)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ftFact26.hours)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ftFact26.count)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ftDiffHours)}
                            </td>
                            <td className={st.textAlignCenter}>
                              {splitNumberDigits(ftDiffCount)}
                            </td>
                          </tr>
                        )
                      })}
                      {(() => {
                        const locTotals = calculateLocationTotals(location)
                        return (
                          <tr className={st.totalRow}>
                            <td className={st.tdTotal} colSpan={3}>
                              Итого: {location.name}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                            >
                              {splitNumberDigits(locTotals.hours_24_25)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                            >
                              {splitNumberDigits(locTotals.count_24_25)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                            >
                              {splitNumberDigits(locTotals.hours_25_26)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                            >
                              {splitNumberDigits(locTotals.count_25_26)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                            >
                              {splitNumberDigits(locTotals.plan_hours_26_27)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                            >
                              {splitNumberDigits(locTotals.plan_count_26_27)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                            >
                              {splitNumberDigits(locTotals.fact_hours_26_27)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                            >
                              {splitNumberDigits(locTotals.fact_count_26_27)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                              style={
                                locTotals.diff_26_27 < 0
                                  ? negativeStyle
                                  : undefined
                              }
                            >
                              {splitNumberDigits(locTotals.diff_26_27)}
                            </td>
                            <td
                              className={`${st.tdTotal} ${st.textAlignCenter}`}
                              style={
                                locTotals.diff_count_26_27 < 0
                                  ? negativeStyle
                                  : undefined
                              }
                            >
                              {splitNumberDigits(locTotals.diff_count_26_27)}
                            </td>
                          </tr>
                        )
                      })()}
                    </React.Fragment>
                  )
                })}
                {locations.length > 0 && (
                  <tr className={st.totalRow}>
                    {(() => {
                      const grandTotals = calculateGrandTotals(locations)
                      const totalTeachers24_25 =
                        data.totalTeachers2024_2025 || grandTotals.count_24_25
                      const totalTeachers25_26 =
                        data.totalTeachers2025_2026 || grandTotals.count_25_26
                      const totalTeachersPlan26_27 =
                        grandTotals.plan_count_26_27
                      const totalTeachersFact26_27 =
                        data.totalTeachers2026_2027 ||
                        grandTotals.fact_count_26_27
                      // Разница = Факт - План
                      const grandDiffCount =
                        totalTeachersFact26_27 - totalTeachersPlan26_27

                      return (
                        <>
                          <td className={st.tdTotal} colSpan={3}>
                            Общее итого
                          </td>
                          <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                            {splitNumberDigits(grandTotals.hours_24_25)}
                          </td>
                          <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                            {splitNumberDigits(totalTeachers24_25)}
                          </td>
                          <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                            {splitNumberDigits(grandTotals.hours_25_26)}
                          </td>
                          <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                            {splitNumberDigits(totalTeachers25_26)}
                          </td>
                          <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                            {splitNumberDigits(grandTotals.plan_hours_26_27)}
                          </td>
                          <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                            {splitNumberDigits(totalTeachersPlan26_27)}
                          </td>
                          <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                            {splitNumberDigits(grandTotals.fact_hours_26_27)}
                          </td>
                          <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                            {splitNumberDigits(totalTeachersFact26_27)}
                          </td>
                          <td
                            className={`${st.tdTotal} ${st.textAlignCenter}`}
                            style={
                              grandTotals.diff_26_27 < 0
                                ? negativeStyle
                                : undefined
                            }
                          >
                            {splitNumberDigits(grandTotals.diff_26_27)}
                          </td>
                          <td
                            className={`${st.tdTotal} ${st.textAlignCenter}`}
                            style={
                              grandDiffCount < 0 ? negativeStyle : undefined
                            }
                          >
                            {splitNumberDigits(grandDiffCount)}
                          </td>
                        </>
                      )
                    })()}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ВТОРАЯ ТАБЛИЦА */}
          {locations.length > 0 && (
            <>
              <div
                style={{
                  marginTop: '30px',
                  marginBottom: '10px',
                  padding: '10px',
                }}
              >
                <h2>Средняя загрузка по учителю</h2>
              </div>
              <div
                className={st.tableWrapper}
                ref={refWrapper}
                style={{ padding: '10px' }}
              >
                <table className={st.table}>
                  <thead className={style.tableHeader}>
                    <tr>
                      <th className={style.stickyColumn} rowSpan={2}>
                        Локация
                      </th>
                      <th rowSpan={2}>PartTime/Fulltime</th>
                      <th rowSpan={2}>Период</th>
                      <th>2024-2025 Факт</th>
                      <th>2025-2026 Факт</th>
                      <th>2026-2027 План</th>
                      <th>2026-2027 Факт</th>
                      <th>2026-2027 Разница</th>
                    </tr>
                    <tr>{/* Пустые ячейки для выравнивания */}</tr>
                  </thead>
                  <tbody className={`${st.tbody} ${st.tbodyStriped}`}>
                    {locations.map((location) => {
                      const months = location.months || []
                      return (
                        <React.Fragment key={`avg-${location.id}`}>
                          {months.map((month, idx) => {
                            const avgPlan = calculateAverage(
                              location.data[`pt_${month.name}_2026_2027_plan`]
                                ?.hours || 0,
                              location.data[`pt_${month.name}_2026_2027_plan`]
                                ?.count || 0,
                            )
                            const avgFact = calculateAverage(
                              location.data[`pt_${month.name}_2026_2027_fact`]
                                ?.hours || 0,
                              location.data[`pt_${month.name}_2026_2027_fact`]
                                ?.count || 0,
                            )
                            // Разница = Факт - План (без подсветки)
                            const diff =
                              Math.round((avgFact - avgPlan) * 10) / 10
                            return (
                              <tr key={`avg-pt-${month.num}`}>
                                {idx === 0 && (
                                  <td
                                    className={getTdClassNames({
                                      classes: [st.color, st.fw500],
                                    })}
                                    rowSpan={months.length * 2}
                                  >
                                    {location.name}
                                  </td>
                                )}
                                {idx === 0 && (
                                  <td rowSpan={months.length}>PT</td>
                                )}
                                <td>{month.name}</td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(
                                    calculateAverage(
                                      location.data[
                                        `pt_${month.name}_2024_2025`
                                      ]?.hours || 0,
                                      location.data[
                                        `pt_${month.name}_2024_2025`
                                      ]?.count || 0,
                                    ),
                                  )}
                                </td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(
                                    calculateAverage(
                                      location.data[
                                        `pt_${month.name}_2025_2026`
                                      ]?.hours || 0,
                                      location.data[
                                        `pt_${month.name}_2025_2026`
                                      ]?.count || 0,
                                    ),
                                  )}
                                </td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(avgPlan)}
                                </td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(avgFact)}
                                </td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(diff)}
                                </td>
                              </tr>
                            )
                          })}
                          {months.map((month, idx) => {
                            const avgPlan = calculateAverage(
                              location.data[`ft_${month.name}_2026_2027_plan`]
                                ?.hours || 0,
                              location.data[`ft_${month.name}_2026_2027_plan`]
                                ?.count || 0,
                            )
                            const avgFact = calculateAverage(
                              location.data[`ft_${month.name}_2026_2027_fact`]
                                ?.hours || 0,
                              location.data[`ft_${month.name}_2026_2027_fact`]
                                ?.count || 0,
                            )
                            // Разница = Факт - План (без подсветки)
                            const diff =
                              Math.round((avgFact - avgPlan) * 10) / 10
                            return (
                              <tr key={`avg-ft-${month.num}`}>
                                {idx === 0 && (
                                  <td rowSpan={months.length}>FT</td>
                                )}
                                <td>{month.name}</td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(
                                    calculateAverage(
                                      location.data[
                                        `ft_${month.name}_2024_2025`
                                      ]?.hours || 0,
                                      location.data[
                                        `ft_${month.name}_2024_2025`
                                      ]?.count || 0,
                                    ),
                                  )}
                                </td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(
                                    calculateAverage(
                                      location.data[
                                        `ft_${month.name}_2025_2026`
                                      ]?.hours || 0,
                                      location.data[
                                        `ft_${month.name}_2025_2026`
                                      ]?.count || 0,
                                    ),
                                  )}
                                </td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(avgPlan)}
                                </td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(avgFact)}
                                </td>
                                <td className={st.textAlignCenter}>
                                  {splitNumberDigits(diff)}
                                </td>
                              </tr>
                            )
                          })}
                          {(() => {
                            const averages = calculateAverageData(location)
                            // Разница = Факт - План
                            const totalDiff =
                              Math.round(
                                (averages.total_fact_26_27.avg -
                                  averages.total_plan_26_27.avg) *
                                  10,
                              ) / 10
                            return (
                              <tr className={st.totalRow}>
                                <td className={st.tdTotal} colSpan={3}>
                                  Итого по локации
                                </td>
                                <td
                                  className={`${st.tdTotal} ${st.textAlignCenter}`}
                                >
                                  {splitNumberDigits(averages.total_24_25.avg)}
                                </td>
                                <td
                                  className={`${st.tdTotal} ${st.textAlignCenter}`}
                                >
                                  {splitNumberDigits(averages.total_25_26.avg)}
                                </td>
                                <td
                                  className={`${st.tdTotal} ${st.textAlignCenter}`}
                                >
                                  {splitNumberDigits(
                                    averages.total_plan_26_27.avg,
                                  )}
                                </td>
                                <td
                                  className={`${st.tdTotal} ${st.textAlignCenter}`}
                                >
                                  {splitNumberDigits(
                                    averages.total_fact_26_27.avg,
                                  )}
                                </td>
                                <td
                                  className={`${st.tdTotal} ${st.textAlignCenter}`}
                                  style={
                                    totalDiff < 0 ? negativeStyle : undefined
                                  }
                                >
                                  {splitNumberDigits(totalDiff)}
                                </td>
                              </tr>
                            )
                          })()}
                        </React.Fragment>
                      )
                    })}
                    {locations.length > 0 && (
                      <tr className={st.totalRow}>
                        {(() => {
                          const grandTotals = calculateGrandTotals(locations)
                          const grandAvg24_25 = calculateAverage(
                            grandTotals.hours_24_25,
                            grandTotals.count_24_25,
                          )
                          const grandAvg25_26 = calculateAverage(
                            grandTotals.hours_25_26,
                            grandTotals.count_25_26,
                          )
                          const grandAvgPlan26_27 = calculateAverage(
                            grandTotals.plan_hours_26_27,
                            grandTotals.plan_count_26_27,
                          )
                          const grandAvgFact26_27 = calculateAverage(
                            grandTotals.fact_hours_26_27,
                            grandTotals.fact_count_26_27,
                          )
                          // Разница = Факт - План
                          const grandDiff =
                            Math.round(
                              (grandAvgFact26_27 - grandAvgPlan26_27) * 10,
                            ) / 10

                          return (
                            <>
                              <td className={st.tdTotal} colSpan={3}>
                                Итого общее
                              </td>
                              <td
                                className={`${st.tdTotal} ${st.textAlignCenter}`}
                              >
                                {splitNumberDigits(grandAvg24_25)}
                              </td>
                              <td
                                className={`${st.tdTotal} ${st.textAlignCenter}`}
                              >
                                {splitNumberDigits(grandAvg25_26)}
                              </td>
                              <td
                                className={`${st.tdTotal} ${st.textAlignCenter}`}
                              >
                                {splitNumberDigits(grandAvgPlan26_27)}
                              </td>
                              <td
                                className={`${st.tdTotal} ${st.textAlignCenter}`}
                              >
                                {splitNumberDigits(grandAvgFact26_27)}
                              </td>
                              <td
                                className={`${st.tdTotal} ${st.textAlignCenter}`}
                                style={
                                  grandDiff < 0 ? negativeStyle : undefined
                                }
                              >
                                {splitNumberDigits(grandDiff)}
                              </td>
                            </>
                          )
                        })()}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для выбранных фильтров</h1>
      )}
    </>
  )
}

export default observer(TeacherPlanReport)
